/* Summing a result column without inventing a zero.
 *
 * These lived inside Summary/OverlayChart.jsx, which is where they were first
 * needed. They moved here when ConsumptionDashboard/ConsumptionChart.jsx needed
 * the same accounting: the two files each carried a `calculateTotalAreaAndCost`
 * of their own and the two had drifted into disagreeing about what a
 * consumption total IS (see utils/consumptionTotals.js), which is what happens
 * to a rule that has two homes. Nothing about the behaviour changed in the
 * move.
 *
 * The rule they encode, stated once:
 *
 *   A column that is not in the file at all must not sum to 0. A card showing
 *   $0.00 because nothing was read is indistinguishable from one showing a
 *   measured zero, and a customer has already reported the fabricated kind as
 *   an accounting bug. Absence surfaces as null, which `hasCardValue` rejects
 *   and the card renders as an em-dash. A genuine 0 -- every EV's Storage
 *   Discharge Value on husky-2040-prod is exactly that -- still reaches the
 *   card as 0 and renders 0.
 *
 * Presence is judged per row as the loop passes it, so it costs nothing extra:
 * `hasBackendTotalsColumn` would walk the whole dataset again for any column
 * that turns out to be absent, which is the case we most need to be cheap.
 */

/** A fresh accumulator: a running total plus "was this column ever there". */
export const startColumnSum = () => ({ total: 0, seen: false });

/** Add one row's cell, recording presence even when the value is not numeric. */
export const addColumnValue = (accumulator, row, columnName) => {
  const raw = row?.[columnName];
  if (raw === undefined || raw === null || raw === "") return;
  accumulator.seen = true;
  const numeric = parseFloat(raw);
  if (Number.isFinite(numeric)) accumulator.total += numeric;
};

/** The summed figure as a fixed-2 string, or null when the column was absent. */
export const finishColumnSum = (accumulator) =>
  accumulator.seen ? accumulator.total.toFixed(2) : null;

/* Sum SEVERAL columns in ONE walk of the rows.
 *
 * `sumBackendColumn` is one pass per column, so the production effects were
 * already walking the dataset twice (energy, then cost) before this existed.
 * Reading a third column with a third call would have been a third walk over
 * 8,784 rows to fetch a neighbour of a cell already in hand.
 *
 * Takes { field: columnName } and returns { field: accumulator }, so picking up
 * an extra column costs one column read per row instead of a whole extra
 * traversal.
 */
export const sumBackendColumns = (rows, columnsByField) => {
  const fields = Object.keys(columnsByField);
  const sums = {};
  for (const field of fields) sums[field] = startColumnSum();
  if (!Array.isArray(rows)) return sums;

  for (const row of rows) {
    if (!row) continue;
    for (const field of fields) {
      addColumnValue(sums[field], row, columnsByField[field]);
    }
  }
  return sums;
};

/** True when any row carries a non-empty cell for this column. */
export const hasBackendTotalsColumn = (rows, columnName) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  for (const row of rows) {
    if (!row) continue;
    const value = row[columnName];
    if (value !== undefined && value !== null && value !== "") return true;
  }
  return false;
};

/* Plain sum, which returns 0 for a column that is not there.
 *
 * Safe ONLY behind a `hasBackendTotalsColumn` check that has already
 * established the column exists -- which is how every caller uses it. Reaching
 * for this without that check is the fabricated zero above. */
export const sumBackendColumn = (rows, columnName) => {
  if (!Array.isArray(rows)) return 0;
  let total = 0;
  for (const row of rows) {
    if (!row) continue;
    const numeric = parseFloat(row[columnName]);
    if (Number.isFinite(numeric)) total += numeric;
  }
  return total;
};
